import { buildTokenHash, ogApiGet, getSubscriptions } from "//bark.co/cdn/shop/t/282/assets/ordergroove.js?v=10848403420202116351788363986";
import { atbUpdateModalTotals, atbBuildOrderSections, getAtbAllProductIds, enrichAtbItems, buildAtbItemsHash } from "//bark.co/cdn/shop/t/282/assets/atb-order-item-html.js?v=105831213652427031231777920619";

/**
 * ATB Collection Module
 * 
 * Optimized for fast loading with minimal blocking.
 */

// Execute immediately for faster loading
(function() {
  'use strict';

  let initializationAttempts = 0;
  const maxAttempts = 50; // ~2.5 seconds at 50fps

  // Check if required elements exist before proceeding
  function checkRequiredElements() {
    const atbContainer = document.querySelector(".atb-container");
    const ogSigDataDiv = document.querySelector(".og-sig-data");
    return atbContainer && (ogSigDataDiv || document.body.classList.contains('bark-sig'));
  }

  // Initialize when elements are ready
  function initializeWhenReady() {
    initializationAttempts++;

    if (checkRequiredElements()) {
      setupEventListeners();
    } else if (initializationAttempts < maxAttempts) {
      // Use requestAnimationFrame for efficient polling
      requestAnimationFrame(initializeWhenReady);
    } else {
      // Fallback to DOMContentLoaded if elements still not ready
      document.addEventListener('DOMContentLoaded', () => {
        setupEventListeners();
      });
    }
  }

  // Start initialization immediately
  initializeWhenReady();
})();

let customerSubscriptionsById = new Map();

function setupEventListeners() {
  document.addEventListener('ognextorderitemsfetched', (event) => {
    const items = event.detail.items;
    buildAtbSection(items);
  });
  setupModalEventListeners();
  setupProductCardEventListeners();
  setupLastShippedOrderEventListeners();
  setupChangeAndUpgradeBoxEventListeners();
}

function setupModalEventListeners() {
  const orderDetailsModalLink = document.querySelector(".atb-order-details-modal-button");

  if (orderDetailsModalLink) {
    orderDetailsModalLink.addEventListener("click", (event) => {
      event.preventDefault();
      Shopify.analytics.publish("rudderstack_track", {
        eventName: "ATB Order Details Modal Opened",
        eventData: {
          daysLeft: daysLeft(),
          orderTotal: document.querySelector(".atb-order-details-modal__total-amount").textContent,
          freeShipping: hasFreeShipping(),
        }
      });

      window.ModalUtility.openBarkcoModal("#atb-order-details-modal", "ATB Collection");
    });
  }
}

function setupProductCardEventListeners() {
  const quickBuyButtons = document.querySelectorAll(".atb-product-carousel .product-card-redesign__quick-buy");

  const addClickHandler = (element, event) => {
    element.addEventListener("click", () => {
      const orderTotal = document.querySelector(
        ".atb-order-details-modal__total-amount"
      ).textContent;
      const freeShipping = hasFreeShipping();

      const productInfo = JSON.parse(
        element.getAttribute("data-amplitude-info")
      );

      const amplitudeInfo = {
        daysLeft: daysLeft(),
        orderTotal,
        freeShipping,
        ...productInfo,
      };

      Shopify.analytics.publish("rudderstack_track", {
        eventName: event,
        eventData: amplitudeInfo
      });
    });
  };

  quickBuyButtons.forEach((button) =>
    addClickHandler(button, "ATB Carousel Quick Add Clicked")
  );
}

function setupLastShippedOrderEventListeners() {
  const trackLink = document.querySelector(".last-shipped-order-info__track-link");
  const orderLink = document.querySelector(".last-shipped-order-info__order-link");
  const loginLink = document.querySelector(".last-shipped-order-info__login-link");

  if (trackLink) {
    trackLink.addEventListener("click", () => {
      Shopify.analytics.publish("rudderstack_track", {
        eventName: "ATB Last Shipped Order Track Clicked"
      });
    });
  }

  if (orderLink) {
    orderLink.addEventListener("click", () => {
      Shopify.analytics.publish("rudderstack_track", {
        eventName: "ATB Last Shipped Order View Clicked"
      });
    });
  }

  if (loginLink) {
    loginLink.addEventListener("click", () => {
      Shopify.analytics.publish("rudderstack_track", {
        eventName: "ATB Last Shipped Order Login Clicked"
      });
    });
  }
}

async function buildAtbSection(items) {
  let customerId;
  try {
    setOgSubLinks();
    const ogSigDataDiv = document.querySelector(".og-sig-data");
    customerId = ogSigDataDiv && ogSigDataDiv.getAttribute('data-attr-customer-id');
    const hasBarkSignature = document.body.classList.contains('bark-sig');

    if (!customerId && !hasBarkSignature) {
      return;
    }

    let tokenHash = buildTokenHash();
    const nextOrder = JSON.parse(sessionStorage.getItem("og-next-order"))
    const orderItems = await fetchOrderItemsWithProducts(tokenHash, items);
    const subscriptions = await getSubscriptions();
    updateCustomerSubscriptions(subscriptions);

    if (nextOrder && orderItems) {
      document.body.classList.add('next-order');
      const atbSection = document.querySelector(".atb-container");
      const classList = atbSection.classList;
      const orderDetailsWrapper = document.querySelector(".upcoming-info-wrapper");
      orderDetailsWrapper?.classList.add("upcoming-info-wrapper--loaded");
      let firstRender = true;
      if (classList.contains("atb-container--loaded")) {
        firstRender = false;
      }
      atbSection.classList.add("atb-container--loaded");

      hideCarouselProductsAlreadyInOrder(orderItems);
      buildOrderDetailsModal(orderItems, nextOrder);
      setShippingDateContent(nextOrder);
      setFreeShippingContent(nextOrder);
      updateDogNames(orderItems);
      await buildLastShippedOrderInfo();
      setupChangeBoxButton(orderItems);

      // We can't rely on the dom values because they might not be updated yet
      const orderTotal = parseFloat(nextOrder.sub_total);
      const daysLeft = nextOrderDaysLeft(nextOrder);
      const freeShippingThreshold = document.querySelector(".free-shipping-data").dataset.freeShippingThreshold;
      const freeShippingThresholdValue = parseFloat(freeShippingThreshold);
      const freeShipping = orderTotal >= freeShippingThresholdValue || nextOrder.shipping_total === "0.00";

      const lastShippedOrderInfo = document.querySelector(".last-shipped-order-info__content");
      const hasTrackingLink = !!lastShippedOrderInfo?.querySelector(".last-shipped-order-info__track-link");
      const hasLoginLink = !!lastShippedOrderInfo?.querySelector(".last-shipped-order-info__login-link");
      const statusValue = lastShippedOrderInfo?.querySelector(".last-shipped-order-info__status-value")?.textContent;
      if (firstRender) {
        Shopify.analytics.publish("rudderstack_track", {
          eventName: "ATB Next Order Module Loaded",
          eventData: {
            daysLeft: daysLeft.toString(),
            orderTotal: `$${orderTotal.toFixed(2)}`,
            freeShipping,
            lastOrderStatus: statusValue,
            lastOrderTrackingLink: hasTrackingLink,
            lastOrderLoginPrompt: hasLoginLink,
          }
        });
      }
    }
  } catch (error) {
    onError(error, { customerId });
  }
}

function setOgSubLinks() {
  const ordergrooveSubLink = document.querySelector(".ordergroove-subscription-link");
  const subManagementLink = document.querySelector(".upcoming-order-manage-subscription-link");
  let subManagementUrl;

  if (ordergrooveSubLink) {
    subManagementUrl = ordergrooveSubLink.getAttribute("data-og-sub-management-link");
  } else {
    subManagementUrl = '/account';
  }

  subManagementLink.href = subManagementUrl;
  subManagementLink.addEventListener("click", () => {
    Shopify.analytics.publish("rudderstack_track", {
      eventName: "ATB Manage Subscription Button Clicked",
      eventData: {
        daysLeft: daysLeft(),
        orderTotal: document.querySelector(".atb-order-details-modal__total-amount").textContent,
        freeShipping: hasFreeShipping(),
      }
    });
  });
}

function setShippingDateContent(nextOrder) {
  const nextOrderDate = new Date(nextOrder.place);
  const nextOrderDateString = nextOrderDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
  const daysLeftNumber = nextOrderDaysLeft(nextOrder);
  const daysLeftEl = document.getElementById("days-left");
  daysLeftEl.setAttribute("data-days-left", daysLeftNumber);

  if (daysLeftNumber <= 1) {
    daysLeftEl.textContent = "Last day";
  } else {
    daysLeftEl.textContent = `${daysLeftNumber} days left`;
  }

  const nextOrderDateEls = document.querySelectorAll(".next-order-date");
  nextOrderDateEls.forEach(el => el.textContent = nextOrderDateString);
}

function nextOrderDaysLeft(nextOrder) {
  const timeZone = 'America/Chicago';

  // datetime string is in "YYYY-MM-DD HH:MM:SS" format. OG uses Central time for processing
  function getCentralMidnightDate(dateTimeString) {
    const dateStr = dateTimeString.split(' ')[0];
    const [year, month, day] = dateStr.split('-').map(Number);

    // month is 0-indexed in Date constructor so subtract 1
    const utcDate = new Date(Date.UTC(year, month - 1, day));

    const offsetMinutes = -new Date(utcDate.toLocaleString('en-US', { timeZone })).getTimezoneOffset();

    return new Date(utcDate.getTime() + offsetMinutes * 60 * 1000);
  }

  const nextOrderDate = getCentralMidnightDate(nextOrder.place);

  const now = new Date();
  const nowInChicago = new Date(now.toLocaleString('en-US', { timeZone }));

  const currentDateInChicago = new Date(
    nowInChicago.getFullYear(),
    nowInChicago.getMonth(),
    nowInChicago.getDate()
  );

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffMs = nextOrderDate.getTime() - currentDateInChicago.getTime();

  return Math.ceil(diffMs / msPerDay);
}

function daysLeft() {
  const daysLeftEl = document.getElementById("days-left");
  return daysLeftEl.getAttribute("data-days-left");
}

function setFreeShippingContent(nextOrder) {
  const freeShippingDataEl = document.querySelector(".free-shipping-data");
  const freeShippingThreshold = freeShippingDataEl.dataset.freeShippingThreshold;
  const freeShippingThresholdValue = parseFloat(freeShippingThreshold);

  const currentOrderValue = parseFloat(nextOrder.sub_total);
  const freeShippingText = document.querySelector(".free-shipping-text");
  const freeShippingAmountLeft = document.querySelector(".free-shipping-amount-left");
  const freeShippingProgressBar = document.querySelector(".free-shipping-progress-bar");
  const freeShippingProgressBarFill = document.querySelector(".free-shipping-progress-bar-fill");
  const freeShippingIcon = document.querySelector(".free-shipping-icon");

  if (currentOrderValue < freeShippingThresholdValue && nextOrder.shipping_total !== "0.00") {
    const amountLeft = freeShippingThresholdValue - currentOrderValue;
    freeShippingText.textContent = `Add to your order for free shipping`;
    freeShippingAmountLeft.textContent = `$${amountLeft.toFixed(2)} AWAY`;
    const progress = (currentOrderValue / freeShippingThresholdValue) * 100;
    freeShippingProgressBarFill.style.width = `${progress}%`;
    freeShippingProgressBar.classList.remove("hidden");
    freeShippingIcon.classList.add("hidden");
  } else {
    freeShippingProgressBar.classList.add("hidden");
    freeShippingAmountLeft.textContent = "";
    freeShippingText.textContent = "This order gets free shipping!";
    freeShippingIcon.classList.remove("hidden");
  }
}

function updateDogNames(orderItems) {
  const dogs = window.customerDogs || [];
  const subscriptionItems = orderItems.filter((item) => item.subscriptionItem);
  const subscriptionIds = subscriptionItems.map((item) => item.subscriptionItemId);
  const activeDogs = dogs.filter(
    (dog) =>
      !dog.passedAt &&
      subscriptionIds.some(
        (id) => dog.subscriptionIds && dog.subscriptionIds.includes(id)
      )
  );

  if (activeDogs.length === 1) {
    const possesiveName = activeDogs[0].name + "'s";
    const welcomeMessageEl = document.getElementById("dynamic-welcome-message");
    const dynamicModalMessageEl = document.getElementById("dynamic-modal-message");
    const upcomingOrderNameTextEl = document.getElementById(
      "upcoming-order-name-text"
    );
    if (welcomeMessageEl) {
      welcomeMessageEl.textContent = `Hi, ${activeDogs[0].name}!`;

      Shopify.analytics.publish("rudderstack_track", {
        eventName: "ATB Next Order Module Loaded With Name",
        eventData: {
          name: activeDogs[0].name,
        }
      });
    }

    if (upcomingOrderNameTextEl) {
      upcomingOrderNameTextEl.textContent = possesiveName;
    }

    if (dynamicModalMessageEl) {
      dynamicModalMessageEl.textContent = `${possesiveName} items will be charged and ship with their`;
    }
  }
}

function buildOrderDetailsModal(orderItems, nextOrder) {
  const promoOfferId = document.querySelector('[data-atb-promo-offer-id]').dataset['atbPromoOfferId'];
  const modal = document.querySelector("#atb-order-details-modal");
  if (!modal) {
    console.error("ATB ORDER DETAILS MODAL: Modal not found");
    return;
  }
  if (nextOrder?.public_id) modal.dataset.orderId = nextOrder.public_id;
  const modalContent = modal.querySelector(".atb-order-details-modal__body");
  const subscriptionItems = orderItems.filter(item => item.subscriptionItem);
  const oneTimeItems = orderItems.filter(item => !item.subscriptionItem);
  const subscriptionItemsProcessed = buildAtbItemsHash(subscriptionItems, promoOfferId);
  const oneTimeItemsProcessed = buildAtbItemsHash(oneTimeItems, promoOfferId)
    .sort((a, b) => {
      if (a.offerId === promoOfferId && b.offerId !== promoOfferId) return 1;
      if (a.offerId !== promoOfferId && b.offerId === promoOfferId) return -1;
      return 0;
    });

  atbUpdateModalTotals(modal, subscriptionItems, oneTimeItems);
  atbBuildOrderSections(modalContent, subscriptionItemsProcessed, oneTimeItemsProcessed, promoOfferId);
}


async function fetchOrderItemsWithProducts(tokenHash, items) {
  const allIds = getAtbAllProductIds(items.data.results);
  const productParams = allIds.map(id => `external_product_ids[]=${id}`).join('&');
  const products = await ogApiGet(`/products/?page_size=100&autoship_enabled=all&${productParams}`, tokenHash);
  return enrichAtbItems(items.data.results, products.data.results);
}

function hideCarouselProductsAlreadyInOrder(orderItems) {
  const atbProductCards = document.querySelectorAll(".atb-product-card");
  const productIds = orderItems.map(item => item.externalId);

  atbProductCards.forEach(card => {
    const variantIds = card.getAttribute("data-variant-ids")?.split(",") || [];

    if (variantIds.some(variantId => productIds.includes(variantId))) {
      card.classList.add("hidden");
    }
  });
}

function updateCustomerSubscriptions(subscriptions = []) {
  customerSubscriptionsById = new Map();

  subscriptions.forEach((subscription) => {
    const publicId = subscription?.public_id;

    if (!publicId) {
      return;
    }

    customerSubscriptionsById.set(publicId, subscription);
  });
}

function hasFreeShipping() {
  const freeShippingProgressBar = document.querySelector(".free-shipping-progress-bar");
  return freeShippingProgressBar.classList.contains("hidden");
}

function onError(error) {
  newrelic.noticeError(error);
}

async function buildLastShippedOrderInfo() {
  const lastShippedOrderInfo = document.querySelector(".last-shipped-order-info");

  if (!lastShippedOrderInfo) {
    return;
  }

  const orderId = lastShippedOrderInfo.getAttribute("data-last-order-id");
  const isCustomerLoggedIn = orderId && orderId !== ""; // Check if customer is logged in by checking if orderId exists
  const hasBarkSignature = document.body.classList.contains('bark-sig');

  // If customer is not logged in but has bark signature, show login prompt
  if (!isCustomerLoggedIn && hasBarkSignature) {
    showLoginPrompt(lastShippedOrderInfo);
    return;
  }

  // If no order ID, don't proceed
  if (!orderId) {
    return;
  }

  try {
    const response = await fetch(`/apps/proxy/order-fulfillment-info?order_id=${orderId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch fulfillment info for order ${orderId}: ${response.statusText}`);
    }

    const fulfillmentData = await response.json();

    if (!fulfillmentData) {
      return;
    }

    // Show the section
    lastShippedOrderInfo.classList.remove("last-shipped-order-info");
    lastShippedOrderInfo.classList.add("last-shipped-order-info--loaded");

    // Update status
    const statusEl = lastShippedOrderInfo.querySelector(".last-shipped-order-info__status-value");
    if (statusEl) {
      statusEl.textContent = fulfillmentData.overallStatus;
    }

    // Update tracking link if primary tracking info exists 
    const trackLink = lastShippedOrderInfo.querySelector(".last-shipped-order-info__track-link");
    if (trackLink && fulfillmentData.trackingUrl) {
      trackLink.classList.remove("last-shipped-order-info__track-link");
      trackLink.classList.add("last-shipped-order-info__track-link--loaded");
      trackLink.href = fulfillmentData.trackingUrl;
    }
  } catch (error) {
    console.error('Error fetching fulfillment info:', error);
  }
}

function showLoginPrompt(lastShippedOrderInfo) {
  // Show the section
  lastShippedOrderInfo.classList.remove("last-shipped-order-info");
  lastShippedOrderInfo.classList.add("last-shipped-order-info--loaded");

  // Hide the status badge
  const statusEl = lastShippedOrderInfo.querySelector(".last-shipped-order-info__status");
  if (statusEl) {
    statusEl.style.display = "none";
  }

  // Rendered by the section, which has `routes`; asset Liquid does not.
  const loginUrl = lastShippedOrderInfo.dataset.loginUrl || "/account/login";

  // Update the content to show login prompt
  const content = lastShippedOrderInfo.querySelector(".last-shipped-order-info__content");
  if (content) {
    content.innerHTML = `
      <div class="last-shipped-order-info__header-wrapper">
        <div class="last-order-title bark-utility-body-lg--bold mb-sm">Last Order</div>
      </div>
      <div class="last-shipped-order-info__login-prompt">
        <p class="last-shipped-order-info__login-text bark-utility-body-md">
          Login to view your last order's shipping status
        </p>
      </div>
      <div class="last-shipped-order-info__actions">
        <a href="${loginUrl}" class="last-shipped-order-info-link last-shipped-order-info__login-link bark-utility-eyebrow-lg">
          Login
        </a>
      </div>
    `;

    // Set up event listener for the login link
    const loginLink = content.querySelector(".last-shipped-order-info__login-link");
    if (loginLink) {
      loginLink.addEventListener("click", () => {
        Shopify.analytics.publish("rudderstack_track", {
          eventName: "ATB Last Shipped Order Login Clicked"
        });
      });
    }
  }
}


function setupChangeAndUpgradeBoxEventListeners() {
  const changeBoxLink = document.querySelector("#change-box-link");

  if (changeBoxLink) {
    changeBoxLink.addEventListener("click", () => {
      Shopify.analytics.publish("rudderstack_track", {
        eventName: "ATB Change Box Button Clicked",
        eventData: {
          daysLeft: daysLeft(),
          orderTotal: document.querySelector(".atb-order-details-modal__total-amount")?.textContent,
          freeShipping: hasFreeShipping(),
        }
      });
    });
  }
}

function setupChangeBoxButton(orderItems) {
  const changeBoxLink = document.querySelector("#change-box-link");

  if (!changeBoxLink || !window.BarkAppUtils) {
    return;
  }
  const playSubItem = orderItems.find(item => isPlaySubscription(item));

  if (playSubItem) {
    changeBoxLink.href = window.BarkAppUtils.generateAppSwapLink(playSubItem.subscriptionItemId);
    changeBoxLink.classList.add("change-box-button--loaded");
  }
}

function isPlaySubscription(orderItem) {
  const extraDataRaw = orderItem.product?.extra_data || ""; // extra_data can be null or a json string

  try {
    const extraData = extraDataRaw ? JSON.parse(extraDataRaw) : {};
    const productType = extraData.product_type?.toLowerCase() || "";
    return productType === "play";
  } catch (e) {
    return false; // If JSON parsing fails, treat it as not a Play subscription
  }
}

